import { DOCUMENT } from '@angular/common';
import { afterRenderEffect, Component, computed, ElementRef, inject, input, linkedSignal, signal, viewChild } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import {
  ChartData,
  Chart,
  ChartDataset,
  ChartOptions,
  ChartType,
  Plugin,
  ScatterDataPoint,
  Tooltip,
  TooltipItem,
  TooltipModel,
  TooltipPositionerFunction,
} from 'chart.js';
import { color, toFont } from 'chart.js/helpers';
import { Graph, GraphType, Point } from '#models';
import { getChartSeriesColors } from '../chart-series-colors';
import type { ChartEventPeriod } from '../../../models/graph';

// Okan is a Nigerian asset; timestamps are displayed in West Africa Time (UTC+1, no DST).
const WAT_TIME_ZONE = 'Africa/Lagos';
const WAT_LABEL = 'WAT';
const TOOLTIP_NUMBER_FORMAT = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 4, useGrouping: false });
const LINE_TOOLTIP_OFFSET_PX = 30;
const TREND_EDGE_TOLERANCE_PX = 6;
const FIRST_OBSERVED_LEAD_MS = 2 * 60 * 60 * 1000;

declare module 'chart.js' {
  interface TooltipPositionerMap {
    timeScrub: TooltipPositionerFunction<ChartType>;
  }
}

Tooltip.positioners.timeScrub = (_items, position) => position;

type RenderableChartType = Exclude<GraphType, 'area' | 'timeGraph'>;

interface ChartViewModel {
  type: RenderableChartType;
  data: ChartData<RenderableChartType, ScatterDataPoint[]>;
  options: ChartOptions<RenderableChartType>;
}

interface ChartTooltipEntry {
  name: string;
  color: string;
  value: string;
  wellCode?: string;
  measurement?: string;
  measurementLabel?: string;
  borderDash?: number[];
}

interface ChartTooltipView {
  title: string;
  entries: ChartTooltipEntry[];
  showNames: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-reusable-chart',
  imports: [BaseChartDirective],
  templateUrl: './reusable-chart.component.html',
  styleUrl: './reusable-chart.component.scss',
})
export class ReusableChartComponent {
  graph = input.required<Graph>();
  height = input<string>('22rem');
  firstObservedAt = input<string | null>(null);
  eventPeriod = input<ChartEventPeriod | null>(null);
  hiddenSeriesIds = input<ReadonlySet<string>>(new Set());
  showLegend = input(true);
  private _document = inject(DOCUMENT);
  private _chart = viewChild(BaseChartDirective);
  private _tooltipElement = viewChild<ElementRef<HTMLElement>>('chartTooltip');
  private _tooltipSize = signal({ width: 0, height: 0 });
  tooltip = linkedSignal<Graph, ChartTooltipView | null>({ source: this.graph, computation: () => null });
  tooltipPosition = computed(() => {
    const tooltip = this.tooltip();
    const size = this._tooltipSize();
    if (!tooltip) return { left: 0, top: 0 };
    let left = tooltip.x;
    if (this.resolveChartType(this.graph().graphType) === 'line') {
      const right = tooltip.x + LINE_TOOLTIP_OFFSET_PX;
      left = right + size.width <= tooltip.width
        ? right : tooltip.x - LINE_TOOLTIP_OFFSET_PX - size.width;
    }
    return {
      left: Math.max(0, Math.min(left, tooltip.width - size.width)),
      top: Math.max(0, Math.min(tooltip.y, tooltip.height - size.height)),
    };
  });
  private _keyboardTimestamp = linkedSignal<Graph, number | null>({ source: this.graph, computation: () => null });
  private _hoverTimestamp: number | null = null;
  private _sortedSeries = computed(() => this.graph().series.map(series => ({
    ...series,
    data: series.data.filter(point => Number.isFinite(point.x) && Number.isFinite(point.y))
      .sort((left, right) => left.x - right.x),
  })));
  // Combined charts start at the latest trend start, ignoring trends that only begin after the
  // first-observed time so a late sensor cannot crop away the onset and its lead-up.
  private _combinedStart = computed(() => {
    if (this.graph().graphType !== 'timeGraph' || this.graph().series.length < 2) return undefined;
    const starts = this._sortedSeries().flatMap(series => series.data.length ? [series.data[0].x] : []);
    if (!starts.length) return undefined;
    const observed = this._firstObservedMs();
    const beforeObserved = observed !== null && Number.isFinite(observed) ? starts.filter(start => start <= observed) : [];
    return Math.max(...(beforeObserved.length ? beforeObserved : starts));
  });
  scrubAnnouncement = signal('');
  private _firstObservedMs = computed(() => {
    const timestamp = this.firstObservedAt();
    return timestamp ? Date.parse(timestamp) : null;
  });
  private _dataBounds = computed(() => {
    const times = this._sortedSeries().flatMap(series => series.data.map(point => point.x));
    return times.length ? { min: Math.min(...times), max: Math.max(...times) } : null;
  });
  private _observedOnChart = computed(() => {
    const timestamp = this._firstObservedMs();
    return this.graph().graphType === 'timeGraph' && timestamp !== null && Number.isFinite(timestamp)
      && this._dataBounds() ? timestamp : null;
  });
  // The axis always reaches the first-observed time so its marker is never hidden by
  // combined-start trimming or by readings that begin or end on the other side of it.
  // When it would sit on the left edge, a lead-in window shows the readings before it.
  private _axisMin = computed(() => {
    const start = this._combinedStart();
    const observed = this._observedOnChart();
    const floor = start ?? this._dataBounds()?.min;
    return observed !== null && floor !== undefined && observed <= floor ? observed - FIRST_OBSERVED_LEAD_MS : start;
  });
  private _axisMax = computed(() => {
    const observed = this._observedOnChart();
    const max = this._dataBounds()?.max;
    return observed !== null && max !== undefined && observed > max ? observed : undefined;
  });
  firstObservedLabel = computed(() => {
    const timestamp = this._firstObservedMs();
    if (timestamp === null || this.graph().graphType !== 'timeGraph') return null;
    if (!Number.isFinite(timestamp)) return 'First observed time is unavailable.';
    return `First observed: ${this.formatTimeValue(timestamp, true)} ${WAT_LABEL}`;
  });
  showFirstObservedLegend = computed(() => this._observedOnChart() !== null);
  highlightAfterFirstObserved = computed(() =>
    this.graph().series.length > 1 && this._observedOnChart() !== null);
  onsetUncertainty = computed(() => {
    const period = this.eventPeriod();
    if (!period) return null;
    const start = Date.parse(period.onsetNotBefore);
    const end = Date.parse(period.onset);
    return Number.isFinite(start) && Number.isFinite(end) && start < end ? { start, end } : null;
  });
  readonly chartPlugins: Plugin<RenderableChartType>[] = [{
    id: 'time-reference-lines',
    beforeDatasetsDraw: chart => {
      const period = this.eventPeriod();
      const observed = this.highlightAfterFirstObserved() ? this._observedOnChart() : null;
      if (this.graph().graphType !== 'timeGraph' || (!period && observed === null)) return;
      const { ctx, chartArea, scales } = chart;
      const theme = getComputedStyle(this._document.documentElement);
      const shade = (start: number, end: number, fill: string) => {
        const from = Math.max(chartArea.left, scales['x'].getPixelForValue(start));
        const to = Math.min(chartArea.right, scales['x'].getPixelForValue(end));
        if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;
        ctx.fillStyle = fill;
        ctx.fillRect(from, chartArea.top, to - from, chartArea.bottom - chartArea.top);
      };
      ctx.save();
      const gray = color(theme.getPropertyValue('--chv-dm-gray').trim() || String(Chart.defaults.color)).alpha(0.12).rgbString();
      const amber = color(theme.getPropertyValue('--status-amber').trim() || String(Chart.defaults.color)).alpha(0.2).rgbString();
      const uncertainty = this.onsetUncertainty();
      if (uncertainty) {
        shade(uncertainty.start, uncertainty.end, gray);
      }
      if (observed !== null) {
        shade(observed, scales['x'].max, amber);
      } else if (period) {
        shade(Date.parse(period.onset), Date.parse(period.transientEnd), amber);
      }
      ctx.restore();
    },
    beforeEvent: (chart, { event, inChartArea }) => {
      if (this.graph().graphType !== 'timeGraph') return;
      this._hoverTimestamp = inChartArea && event.type !== 'mouseout' && typeof event.x === 'number'
        ? chart.scales['x'].getValueForPixel(event.x) ?? null : null;
    },
    afterDatasetsDraw: chart => {
      if (this.resolveChartType(this.graph().graphType) !== 'line') return;
      const { ctx, chartArea, scales } = chart;
      const drawLine = (x: number, stroke: string, width: number, dashed: boolean) => {
        ctx.beginPath();
        ctx.strokeStyle = stroke;
        ctx.lineWidth = width;
        ctx.setLineDash(dashed ? [6, 4] : []);
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
      };
      ctx.save();
      const observed = this._observedOnChart();
      if (observed !== null && observed >= scales['x'].min && observed <= scales['x'].max) {
        const x = scales['x'].getPixelForValue(observed);
        // A light outline keeps the requested black marker visible on dark charts.
        drawLine(x, 'white', 4, true);
        drawLine(x, 'black', 2, true);
      }
      const hovered = chart.tooltip?.opacity
        ? this._hoverTimestamp ?? chart.tooltip.dataPoints?.[0]?.parsed.x : undefined;
      if (typeof hovered === 'number' && hovered >= scales['x'].min && hovered <= scales['x'].max
        && hovered !== observed) {
        const x = scales['x'].getPixelForValue(hovered);
        drawLine(x, 'white', 3, false);
        drawLine(x, 'black', 1, false);
      }
      ctx.restore();
    },
  }];
  selectedSeries = linkedSignal<Graph, ReadonlySet<number>>({ source: this.graph, computation: () => new Set<number>() });
  coloredSeries = computed(() => {
    const theme = getComputedStyle(this._document.documentElement);
    const palette = getChartSeriesColors(theme);
    return this.graph().series.map((series, index) => ({
      ...series, color: series.color ?? (palette[index % palette.length] || Chart.defaults.color as string),
    }));
  });

  constructor() {
    afterRenderEffect(() => {
      this.tooltip();
      const element = this._tooltipElement()?.nativeElement;
      if (!element) return;
      const { width, height } = element.getBoundingClientRect();
      const previous = this._tooltipSize();
      if (width !== previous.width || height !== previous.height) this._tooltipSize.set({ width, height });
    });
  }

  toggleSeries(index: number): void {
    if (this.graph().series.length < 2) return;
    this.selectedSeries.update(current => {
      const next = new Set(current);
      if (!next.delete(index)) next.add(index);
      return next;
    });
  }

  clearSeriesSelection(): void {
    this.selectedSeries.set(new Set<number>());
  }

  private isSeriesShown(index: number): boolean {
    const selected = this.selectedSeries();
    return !this.hiddenSeriesIds().has(this.graph().series[index].id) && (selected.size === 0 || selected.has(index));
  }

  onChartKeydown(event: KeyboardEvent): void {
    if (this.resolveChartType(this.graph().graphType) !== 'line') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.clearScrub();
      return;
    }
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const chart = this._chart()?.chart;
    if (!chart) return;
    const times = [...new Set(this.graph().series.flatMap(series => series.data
      .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y))
      .map(point => point.x)))].filter(time => time >= (this._axisMin() ?? -Infinity)).sort((a, b) => a - b);
    if (!times.length) return;
    event.preventDefault();
    const current = chart.tooltip?.dataPoints?.[0]?.parsed.x ?? this._keyboardTimestamp();
    const previousIndex = current === null ? -1 : times.indexOf(current);
    const index = event.key === 'Home' ? 0
      : event.key === 'End' ? times.length - 1
      : previousIndex < 0 ? (event.key === 'ArrowLeft' ? times.length - 1 : 0)
      : Math.max(0, Math.min(times.length - 1, previousIndex + (event.key === 'ArrowLeft' ? -1 : 1)));
    const timestamp = times[index];
    this._keyboardTimestamp.set(timestamp);
    this._hoverTimestamp = timestamp;
    const active = this.graph().series.flatMap((series, datasetIndex) => series.data.flatMap((point, dataIndex) =>
      point.x === timestamp && Number.isFinite(point.y) ? [{ datasetIndex, index: dataIndex }] : []));
    chart.setActiveElements(active);
    chart.tooltip?.setActiveElements(active, { x: chart.scales['x'].getPixelForValue(timestamp), y: chart.chartArea.top });
    chart.render();
    const title = this.graph().graphType === 'timeGraph' ? `${this.formatTimeValue(timestamp, true)} ${WAT_LABEL}` : String(timestamp);
    const values = this.graph().graphType === 'timeGraph' ? this.recordedTooltipLines(timestamp) : active
      .filter(({ datasetIndex }) => this.isSeriesShown(datasetIndex)).map(({ datasetIndex, index }) => {
      const series = this.graph().series[datasetIndex];
      return `${series.name}: ${TOOLTIP_NUMBER_FORMAT.format(series.data[index].y)} ${this.graph().yAxis.uom}`.trim();
    });
    this.scrubAnnouncement.set(`${title}. ${values.join('; ')}`);
  }

  clearScrub(): void {
    this.tooltip.set(null);
    const chart = this._chart()?.chart;
    this._hoverTimestamp = null;
    chart?.setActiveElements([]);
    chart?.tooltip?.setActiveElements([], { x: 0, y: 0 });
    chart?.render();
    this._keyboardTimestamp.set(null);
    this.scrubAnnouncement.set('');
  }

  chartViewModel = computed<ChartViewModel>(() => {
    const graph = this.graph();
    const chartType = this.resolveChartType(graph.graphType);
    const selected = this.selectedSeries();

    return {
      type: chartType,
      data: {
        datasets: this.coloredSeries().map((series, index): ChartDataset<RenderableChartType, ScatterDataPoint[]> => {
          const dimmed = selected.size > 0 && !selected.has(index);
          return {
            label: series.name,
            hidden: this.hiddenSeriesIds().has(series.id),
            borderDash: series.borderDash,
            data: series.data.map((point) => this.toScatterPoint(point)),
            borderColor: dimmed ? color(series.color).alpha(0.15).rgbString() : series.color,
            backgroundColor: dimmed ? color(series.color).alpha(0.15).rgbString() : series.color,
            ...(selected.size > 0 ? { borderWidth: dimmed ? 3 : 4, order: dimmed ? 0 : -1 } : {}),
            fill: graph.graphType === 'area',
            tension: chartType === 'line' ? 0.3 : 0,
            pointRadius: graph.graphType === 'scatter' ? 4 : 2,
            parsing: false,
          };
        }),
      },
      options: this.buildOptions(graph),
    };
  });

  hasRenderableData = computed(() => {
    const graph = this.graph();
    return graph.series.some((series) => series.data.length > 0);
  });

  formatTimeValueForPeriod(value: string): string {
    return this.formatTimeValue(Date.parse(value), true);
  }

  private buildOptions(graph: Graph): ChartOptions<RenderableChartType> {
    const isTimeGraph = graph.graphType === 'timeGraph';

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      ...(this.resolveChartType(graph.graphType) === 'line'
        ? { interaction: { mode: 'nearest' as const, axis: 'x' as const, intersect: false } } : {}),
      scales: {
        x: {
          type: 'linear',
          ...(this._axisMin() !== undefined ? { min: this._axisMin() } : {}),
          ...(this._axisMax() !== undefined ? { max: this._axisMax() } : {}),
          title: {
            display: true,
            text: this.formatAxisLabel(this.resolveXAxisLabel(graph), this.resolveXAxisUom(graph)),
          },
          ticks: isTimeGraph
            ? { callback: (value: string | number) => this.formatTimeValue(Number(value)) }
            : {},
        },
        y: {
          type: 'linear',
          title: {
            display: true,
            text: this.formatAxisLabel(graph.yAxis.label, graph.yAxis.uom),
          },
        },
      },
      plugins: {
        title: {
          display: Boolean(graph.title),
          text: graph.title,
        },
        legend: {
          display: graph.series.length < 2 && !this.showFirstObservedLegend(),
          position: 'top',
        },
        tooltip: {
          enabled: false,
          external: ({ chart, tooltip }) => this.updateTooltip(chart, tooltip),
          ...(isTimeGraph ? {
            position: 'timeScrub' as const, displayColors: false,
            bodyFont: Chart.defaults.font,
            titleFont: { ...Chart.defaults.font, weight: 'bold' as const },
          } : {}),
          ...(this.resolveChartType(graph.graphType) === 'line'
            ? { filter: (item: TooltipItem<RenderableChartType>, _index: number, items: TooltipItem<RenderableChartType>[]) =>
              isTimeGraph ? _index === 0 : item.parsed.x === items[0]?.parsed.x } : {}),
          callbacks: {
            ...(isTimeGraph ? { title: (items: TooltipItem<RenderableChartType>[]) => {
              const timestamp = this._hoverTimestamp ?? items[0]?.parsed.x;
              return typeof timestamp === 'number'
                ? this.wrapTooltipLines([`${this.formatTimeValue(timestamp, true)} ${WAT_LABEL}`], true) : [];
            } } : {}),
            label: (context: TooltipItem<RenderableChartType>) =>
              this.formatTooltipLabel(context, isTimeGraph),
          },
        },
      },
    };
  }

  private resolveChartType(graphType: GraphType): RenderableChartType {
    if (graphType === 'area' || graphType === 'timeGraph') {
      return 'line';
    }
    return graphType;
  }

  private formatAxisLabel(label: string, uom: string): string {
    const normalizedLabel = label?.trim() || '';
    const normalizedUom = uom?.trim() || '';

    if (!normalizedUom) {
      return normalizedLabel;
    }

    return `${normalizedLabel} (${normalizedUom})`;
  }

  private toScatterPoint(point: Point): ScatterDataPoint {
    return {
      x: point.x,
      y: point.y,
    };
  }

  private resolveXAxisLabel(graph: Graph): string {
    if (graph.graphType !== 'timeGraph') {
      return graph.xAxis.label;
    }

    // Drop the source-zone annotation (e.g. "(UTC)") so only the WAT zone remains.
    return graph.xAxis.label.replace(/\s*\([^)]*\)\s*$/, '').trim();
  }

  private resolveXAxisUom(graph: Graph): string {
    return graph.graphType === 'timeGraph' ? WAT_LABEL : graph.xAxis.uom;
  }

  private formatTimeValue(ms: number, precise = false): string {
    return new Date(ms).toLocaleString(undefined, {
      timeZone: WAT_TIME_ZONE,
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(precise ? { year: 'numeric', second: '2-digit' } : {}),
    });
  }

  private formatTooltipLabel(context: TooltipItem<RenderableChartType>, isTimeGraph: boolean): string | string[] {
    const parsed = context.parsed as { x: number; y: number };
    if (isTimeGraph) return this.wrapTooltipLines(this.recordedTooltipLines(this._hoverTimestamp ?? parsed.x));
    const label = context.dataset.label || 'Series';
    return `${label}: (${TOOLTIP_NUMBER_FORMAT.format(parsed.x)}, ${TOOLTIP_NUMBER_FORMAT.format(parsed.y)})`;
  }

  private recordedTooltipEntries(timestamp: number): ChartTooltipEntry[] {
    const units = this.graph().yAxis.uom?.trim();
    const colors = this.coloredSeries();
    const sorted = this._sortedSeries();
    const scale = sorted.length > 1 ? this._chart()?.chart?.scales['x'] : undefined;
    const tolerance = scale?.width ? (scale.max - scale.min) / scale.width * TREND_EDGE_TOLERANCE_PX : 0;
    const entries: ChartTooltipEntry[] = sorted.flatMap((series, index) => {
      if (!this.isSeriesShown(index)) return [];
      const entry = {
        name: series.name || 'Series',
        color: colors[index].color,
        wellCode: sorted.length > 1 ? series.wellCode : undefined,
        measurement: series.measurement,
        measurementLabel: series.measurementLabel,
        borderDash: series.borderDash,
      };
      const data = series.data;
      if (!data.length) return [{ ...entry, value: 'no recorded readings' }];
      // In combined charts, a trend has no value outside the span of its own readings.
      if (sorted.length > 1
        && (timestamp < data[0].x - tolerance || timestamp > data[data.length - 1].x + tolerance)) return [];
      let low = 0;
      let high = data.length;
      while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (data[mid].x < timestamp) low = mid + 1;
        else high = mid;
      }
      const after = data[low];
      const before = data[low - 1];
      const sample = before && (!after || timestamp - before.x <= after.x - timestamp) ? before : after ?? data[0];
      return [{
        ...entry,
        value: `${TOOLTIP_NUMBER_FORMAT.format(sample.y)}${units ? ` ${units}` : ''}`,
      }];
    });
    const groups = new Map<string, { entry: ChartTooltipEntry; values: Set<string> }>();
    entries.forEach((entry, index) => {
      const key = entry.wellCode && entry.measurementLabel
        ? JSON.stringify([entry.wellCode, entry.measurement ?? entry.measurementLabel])
        : String(index);
      const group = groups.get(key);
      if (group) {
        group.values.add(entry.value);
      } else {
        groups.set(key, { entry, values: new Set([entry.value]) });
      }
    });
    return [...groups.values()].map(({ entry, values }) => ({
      ...entry,
      value: [...values].filter(value => values.size === 1 || value !== 'no recorded readings').join(', '),
    }));
  }

  private recordedTooltipLines(timestamp: number): string[] {
    return this.recordedTooltipEntries(timestamp).map(entry =>
      `${entry.wellCode && entry.measurementLabel ? `${entry.wellCode}, ${entry.measurementLabel}` : entry.name}: ${entry.value}`);
  }

  private updateTooltip(chart: Chart, tooltip: TooltipModel<RenderableChartType>): void {
    if (!tooltip.opacity || !tooltip.dataPoints.length) {
      this.tooltip.set(null);
      return;
    }
    const isTimeGraph = this.graph().graphType === 'timeGraph';
    const timestamp = this._hoverTimestamp ?? tooltip.dataPoints[0].parsed.x;
    const colors = this.coloredSeries();
    const entries = isTimeGraph && timestamp !== null ? this.recordedTooltipEntries(timestamp) : tooltip.dataPoints
      .filter(item => this.isSeriesShown(item.datasetIndex)).map(item => ({
        name: item.dataset.label || 'Series',
        color: colors[item.datasetIndex].color,
        value: item.parsed.x === null || item.parsed.y === null ? 'no recorded readings'
          : `(${TOOLTIP_NUMBER_FORMAT.format(item.parsed.x)}, ${TOOLTIP_NUMBER_FORMAT.format(item.parsed.y)})`,
      }));
    if (!entries.length) {
      this.tooltip.set(null);
      return;
    }
    this.tooltip.set({
      title: isTimeGraph
        ? timestamp === null ? 'Time unavailable' : `${this.formatTimeValue(timestamp, true)} ${WAT_LABEL}`
        : tooltip.title.join(' '),
      entries,
      showNames: this.resolveChartType(this.graph().graphType) !== 'line',
      x: chart.canvas.offsetLeft + tooltip.caretX,
      y: chart.canvas.offsetTop + tooltip.caretY,
      width: chart.canvas.parentElement?.clientWidth || chart.width,
      height: chart.canvas.parentElement?.clientHeight || chart.height,
    });
  }

  private wrapTooltipLines(lines: string[], bold = false): string[] {
    const chart = this._chart()?.chart;
    if (!chart) return lines;
    const { ctx } = chart;
    const maxWidth = Math.max(1, chart.width - 24);
    ctx.save();
    ctx.font = toFont({ ...Chart.defaults.font, ...(bold ? { weight: 'bold' as const } : {}) }).string;
    const wrapped = lines.flatMap(text => {
      const result: string[] = [];
      let line = '';
      for (const character of text) {
        if (line && ctx.measureText(line + character).width > maxWidth) {
          const space = line.lastIndexOf(' ');
          if (space > 0) {
            result.push(line.slice(0, space));
            line = line.slice(space + 1);
          } else {
            result.push(line);
            line = '';
          }
        }
        line += character;
      }
      if (line) result.push(line.trim());
      return result;
    });
    ctx.restore();
    return wrapped;
  }
}
